import { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";

const initial = {title:"", phase_name:"", responsible:"", objective:"", resources:"", criticality:"",planned_start:"",planned_end:""};
const weekEnd = (start) => { const d = new Date(start+"T12:00:00"); d.setDate(d.getDate()+6); return [d.getFullYear(),String(d.getMonth()+1).padStart(2,"0"),String(d.getDate()).padStart(2,"0")].join("-"); };
const monday = () => {
  const d = new Date();
  d.setDate(d.getDate() - (d.getDay() + 6) % 7);
  return [d.getFullYear(), String(d.getMonth()+1).padStart(2,"0"), String(d.getDate()).padStart(2,"0")].join("-");
};
export default function WeeklyWorkPlan({siteId, phases, onChanged}) {
  const [week,setWeek] = useState(monday);
  const [items,setItems] = useState([]);
  const [draft,setDraft] = useState(initial);
  const [error,setError] = useState("");
  const [busy,setBusy] = useState(false);
  const [editingId,setEditingId] = useState(null);
  const [deletingId,setDeletingId] = useState(null);
  const load = useCallback(async () => {
    try { const result = await api.weeklyGantt(siteId,week); setItems(result.items || []); setError(""); }
    catch { setError("Impossibile caricare la settimana"); }
  },[siteId,week]);
  useEffect(() => { load(); },[load]);
  const add = async e => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {...draft,phase_name:draft.phase_name || null,week_start:week,status:editingId ? (items.find(x=>x.id===editingId)?.status || "planned") : "planned",planned_start:draft.planned_start||null,planned_end:draft.planned_end||null};
      if (editingId) await api.updateWeeklyGantt(siteId,editingId,payload);
      else await api.createWeeklyGantt(siteId,payload);
      setEditingId(null); setDraft(initial); await load(); if (onChanged) await onChanged();
    } catch (err) { setError(err?.response?.data?.detail || "Errore salvataggio"); }
    finally { setBusy(false); }
  };
  const remove = async item => {
    if (!window.confirm("Confermi eliminazione lavorazione?")) return;
    setDeletingId(item.id);
    try {
      await api.deleteWeeklyGantt(siteId,item.id);
      if (editingId === item.id) { setEditingId(null); setDraft(initial); }
      await load();
      if (onChanged) await onChanged();
    } catch (err) { setError(err?.response?.data?.detail || "Errore eliminazione"); }
    finally { setDeletingId(null); }
  };
  const updateStatus = async (item,status) => {
    try {
      const {title,phase_name,responsible,objective,resources,criticality,week_start,planned_start,planned_end} = item;
      await api.updateWeeklyGantt(siteId,item.id,{title,phase_name,responsible,objective,resources,criticality,week_start,planned_start,planned_end,status});
      await load(); if (onChanged) await onChanged();
    } catch { setError("Errore aggiornamento"); }
  };
  return <section className="rounded-xl border bg-white p-4 space-y-3" data-testid="weekly-plan">
    <div className="flex justify-between gap-3 flex-wrap">
      <h3 className="font-semibold">Programmazione settimanale</h3>
      <label>Settimana (lunedì) <input type="date" className="border rounded p-1" value={week} onChange={e=>{ const value=e.target.value; if (!value) return; const day=new Date(value+"T12:00:00"); day.setDate(day.getDate()-(day.getDay()+6)%7); setWeek([day.getFullYear(),String(day.getMonth()+1).padStart(2,"0"),String(day.getDate()).padStart(2,"0")].join("-")); }}/></label>
    </div>
    <form onSubmit={add} className="grid grid-cols-1 md:grid-cols-2 gap-2">
      <input required maxLength={240} className="border rounded p-2" placeholder="Lavorazione" value={draft.title} onChange={e=>setDraft({...draft,title:e.target.value})}/>
      <select className="border rounded p-2" value={draft.phase_name} onChange={e=>setDraft({...draft,phase_name:e.target.value})}>
        <option value="">Senza fase CME</option>
        {phases.map(p=><option key={p.name} value={p.name}>{p.name}</option>)}
      </select>
      <label className="text-sm">Inizio<input type="date" className="border rounded p-2 w-full" value={draft.planned_start} min={week} max={weekEnd(week)} onChange={e=>setDraft({...draft,planned_start:e.target.value})}/></label>
      <label className="text-sm">Fine<input type="date" className="border rounded p-2 w-full" value={draft.planned_end} min={draft.planned_start||week} max={weekEnd(week)} onChange={e=>setDraft({...draft,planned_end:e.target.value})}/></label>
      {["responsible","objective","resources","criticality"].map((key)=><input key={key} className="border rounded p-2" maxLength={key==="responsible"?160:1000} placeholder={({responsible:"Responsabile",objective:"Obiettivo",resources:"Risorse",criticality:"Criticità"})[key]} value={draft[key]} onChange={e=>setDraft({...draft,[key]:e.target.value})}/>)}
      <button disabled={busy} className="bg-orange-600 text-white rounded p-2">{editingId ? "Salva modifiche" : "Aggiungi lavorazione"}</button>
      {editingId && <button type="button" className="border rounded p-2" onClick={()=>{setEditingId(null);setDraft(initial);}}>Annulla modifica</button>}
    </form>
    {error && <p role="alert" className="text-red-600">{error}</p>}
    {items.length===0 && <p className="text-slate-500 text-sm">Nessuna lavorazione pianificata.</p>}
    {items.map(item=><div key={item.id} className="border-t pt-2 flex justify-between gap-2">
      <div><strong>{item.title}</strong><p className="text-xs text-slate-500">{[item.phase_name,item.responsible,item.planned_start && (item.planned_start+" → "+item.planned_end),item.objective,item.resources].filter(Boolean).join(" · ")}</p>{item.criticality && <p className="text-xs text-amber-700">{item.criticality}</p>}</div>
      <button type="button" className="text-xs text-orange-700 underline" onClick={()=>{setEditingId(item.id);setWeek(item.week_start);setDraft({title:item.title||"",phase_name:item.phase_name||"",responsible:item.responsible||"",objective:item.objective||"",resources:item.resources||"",criticality:item.criticality||"",planned_start:item.planned_start||"",planned_end:item.planned_end||""});}}>Modifica</button>
      <button type="button" onClick={() => remove(item)}>Elimina</button>
      <select aria-label={"Stato "+item.title} value={item.status} onChange={e=>updateStatus(item,e.target.value)} className="border rounded p-1">
        <option value="planned">Pianificata</option><option value="in_progress">In corso</option><option value="done">Completata</option><option value="blocked">Bloccata</option>
      </select>
    </div>)}
  </section>;
}
